import React, { useMemo, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
  Box,
  Card,
  Button,
  InputAdornment,
  MenuItem,
  Paper,
  Dialog,
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import {
  useCreateRescheduleMutation,
  useGetTeamQuery,
} from "../../../Api/attoDeskApi";
import CloseIcon from "@mui/icons-material/Close";
import {
  ITeam,
  Schedule,
  ScheduleEdit,
} from "../../../Api/Interface/api.interface";
import { useNavigate } from "react-router-dom";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { CiSearch } from "react-icons/ci";

interface ITeampopup {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data: ScheduleEdit;
}

type FormValues = {
  id: number;
  empid: string;
  startTime: string;
  endTime: string;
  hourlySalary: string;
  date: Dayjs;
};

const Team = ({ openModel = false, handleCloseDialog, data }: ITeampopup) => {
  const classes = useStyles();
  const theme = useTheme();
  const [open] = React.useState(openModel);
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [newCompany, { isLoading }] = useCreateRescheduleMutation();
  useState<boolean>(false);
  const handleClose = () => {
    handleCloseDialog(false);
  };

  const { data: tenantData, isLoading: tenantIsLoading } = useGetTeamQuery({
    searchText: "",
    id: "",
  });

  const teamList = useMemo(() => {
    return tenantData?.data as ITeam[];
  }, [tenantData?.data]);

  const teamMap = useMemo(() => {
    const map = new Map<number, { name: string; image: string }>(); // Map to hold id as key and an object with name and image as value
    teamList?.forEach((teamMember) => {
      const fullName = `${teamMember.teamFirstName || ""} ${
        teamMember?.teamLastName || ""
      }`;
      map.set(teamMember.id, { name: fullName, image: teamMember.teamImg });
    });
    return map;
  }, [teamList]);
  const formik = useFormik<FormValues>({
    initialValues: {
      id: data.id,
      empid: String(data.empid),
      startTime: data.startTime,
      endTime: data.endTime,
      hourlySalary: data.hourlySalary,
      date: data.date ? dayjs(data.date.toString()) : dayjs(),
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const updatedData = {
          id: values.id,
          empid: values.empid,
          date: values.date.format("YYYY-MM-DD"),
          startTime: values.startTime,
          endTime: values.endTime,
          hourlySalary: values.hourlySalary,
        };

        const response = await newCompany(updatedData).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          handleClose();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return formik.values.empid === "" ||
      formik.values.startTime === "" ||
      formik.values.endTime === "" ||
      !formik.values.date ||
      !dayjs(formik.values.date).isValid()
      ? false
      : true;
  }, [formik.values]);

  return (
    <Dialog
      open={open}
      PaperProps={{
        style: {
          maxWidth: "1700px",
        },
      }}
    >
      <Grid className={classes.Dialog}>
        <Grid container spacing={3}>
          <Grid item lg={3} md={3} sm={12} xs={12}>
            <Card className={classes.stepperCard}>
              <Grid
                container
                justifyContent={"space-between"}
                spacing={5}
                sx={{ borderBottom: 2, padding: 2 }}
                className={classes.titleGrid}
              >
                <Grid
                  item
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  pt={2}
                  alignContent={"center"}
                >
                  <Typography className={classes.cardTitle}>
                    Create Reschedule
                  </Typography>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item lg={9} md={9} sm={12} xs={12}>
            <FormikProvider value={formik}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 2, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            Schedule Information ({" "}
                            {teamMap.get(data.empid)?.name || ""})
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={3} p={"24px"}>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item lg={6} md={6} sm={12} xs={12}>
                              <Grid container alignItems="center">
                                <Grid item lg={12} md={12} sm={12} xs={12}>
                                  <Typography variant="subtitle1">
                                    Schedule Date
                                  </Typography>
                                </Grid>
                                <Grid item lg={12} md={12} sm={12} xs={12}>
                                  <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                  >
                                    <DatePicker
                                      value={formik.values.date}
                                      onChange={(date) =>
                                        formik.setFieldValue("date", date)
                                      }
                                      slotProps={{
                                        textField: {
                                          placeholder: "Schedule Date",
                                          size: "small",
                                          sx: {
                                            width: "100%",
                                          },
                                          InputProps: {
                                            sx: {
                                              fontSize: 14,
                                            },
                                          },
                                          InputLabelProps: {
                                            sx: {
                                              fontSize: 14,
                                            },
                                          },
                                        },
                                      }}
                                    />
                                  </LocalizationProvider>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Start Time
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Start Time"
                                size="small"
                                type="time"
                                {...formik.getFieldProps("startTime")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                End Time
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="End Time"
                                size="small"
                                type="time"
                                {...formik.getFieldProps("endTime")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        {/* <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Hourly Salary
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Enter Hourly Salary"
                                size="small"
                                {...formik.getFieldProps("hourlySalary")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid> */}
                      </Grid>
                    </Card>
                  </Grid>

                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={"24px"}>
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            className={classes.addButton}
                            onClick={handleClose}
                          >
                            Cancel
                          </Button>
                          <Box m={0.5}></Box>

                          <Button
                            variant="contained"
                            startIcon={
                              isLoading ? (
                                <CircularProgress
                                  size={18}
                                  className={classes.isLoading}
                                />
                              ) : (
                                <SaveAltIcon />
                              )
                            }
                            className={classes.addButton}
                            onClick={() => formik.handleSubmit()}
                            disabled={!formValid}
                          >
                            Save
                          </Button>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                </Grid>
              </form>
            </FormikProvider>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default Team;
